import React, { useCallback, useState, useEffect, useMemo } from 'react';
import {
  CommandSelect,
  CommandSelectTrigger,
  CommandSelectValue,
  CommandSelectContent,
  CommandSelectItem,
  CommandSelectGroup,
  CommandSelectAll,
  CommandSelectEmpty,
  useCommandSelectSearch,
} from './command-select';

// Post-type string literals taken from filtersFormSchema.
export type PostTypeValue =
  | 'INSTAGRAM_POST'
  | 'INSTAGRAM_REEL'
  | 'INSTAGRAM_STORY'
  | 'TIKTOK_VIDEO'
  | 'YOUTUBE_VIDEO'
  | 'YOUTUBE_SHORT'
  | 'PINTEREST_PIN';

export interface PostTypesSelectProps {
  children: React.ReactNode;
}

// Helper: map a post-type value to its social network group label
const mapToGroupLabel = (value: PostTypeValue): string => {
  if (value.startsWith('INSTAGRAM')) return 'Instagram';
  if (value.startsWith('TIKTOK')) return 'TikTok';
  if (value.startsWith('YOUTUBE')) return 'YouTube';
  if (value.startsWith('PINTEREST')) return 'Pinterest';
  return 'Other';
};

/**
 * PostTypesSelect renders a multi-select dropdown of post types.
 */
export const PostTypesSelect: React.FC<PostTypesSelectProps> = ({ children }) => {
  return (
    <div className="relative">
      {children}
    </div>
  );
};

export const PostTypesOptions = ({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: PostTypeValue; label: string }>;
  value: PostTypeValue[];
  onChange: (values: PostTypeValue[]) => void;
}) => {
  const [localValue, setLocalValue] = useState<PostTypeValue[]>(value);
  const [isUpdating, setIsUpdating] = useState(false);

  // Sync local state with prop value
  useEffect(() => {
    if (!isUpdating) {
      // Only update if arrays are actually different (deep equality check)
      const arraysAreEqual = localValue.length === value.length &&
        localValue.every((val, index) => val === value[index]);

      if (!arraysAreEqual) {
        setLocalValue(value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isUpdating]); // localValue intentionally excluded to prevent infinite loop

  // Unified update handler with proper state management
  const updateSelection = useCallback(
    (newSelection: PostTypeValue[]) => {
      setIsUpdating(true);
      setLocalValue(newSelection);
      onChange(newSelection);

      // Reset updating flag after state has settled
      setTimeout(() => {
        setIsUpdating(false);
      }, 0);
    },
    [onChange],
  );

  const handleDismiss = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      updateSelection([]);
    },
    [updateSelection],
  );

  const getDisplayText = () => {
    if (localValue.length === 0) return { mainText: 'Select post types...', count: null };
    if (localValue.length === 1) {
      const postType = options.find((opt) => opt.value === localValue[0]);
      return { mainText: postType?.label || localValue[0], count: null };
    }
    const firstPostType = options.find((opt) => opt.value === localValue[0]);
    return {
      mainText: firstPostType?.label || localValue[0],
      count: localValue.length - 1
    };
  };

  return (
    <CommandSelect>
      <CommandSelectTrigger placeholder="Select post types..." data-dd-action-name="social-analytics-post-type-select-trigger">
        <CommandSelectValue
          placeholder="Select post types..."
          displayText={localValue.length > 0 ? getDisplayText().mainText : undefined}
          count={getDisplayText().count}
          onClear={handleDismiss}
          disabled={isUpdating}
          data-dd-action-name="social-analytics-post-type-select-clear"
        />
      </CommandSelectTrigger>
      <CommandSelectContent searchPlaceholder="Search post types..." maxHeight="200px" useWrapper={false}>
        <PostTypesContentWithSearch
          options={options}
          localValue={localValue}
          isUpdating={isUpdating}
          updateSelection={updateSelection}
        />
      </CommandSelectContent>
    </CommandSelect>
  );
};

// Component that uses the search context for filtering and grouping
const PostTypesContentWithSearch = ({
  options,
  localValue,
  isUpdating,
  updateSelection,
}: {
  options: Array<{ value: PostTypeValue; label: string }>;
  localValue: PostTypeValue[];
  isUpdating: boolean;
  updateSelection: (newSelection: PostTypeValue[]) => void;
}) => {
  // Get the search query from the CommandSelect context
  const searchQuery = useCommandSelectSearch();

  // Group options in stable order
  const groupedOptions = useMemo(() => {
    const groups: Record<string, typeof options> = {};
    options.forEach((opt) => {
      const groupLabel = mapToGroupLabel(opt.value);
      if (!groups[groupLabel]) groups[groupLabel] = [];
      groups[groupLabel].push(opt);
    });
    return groups;
  }, [options]);

  // Filter groups based on search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery) return groupedOptions;

    const query = searchQuery.toLowerCase();
    const filtered: Record<string, typeof options> = {};

    Object.entries(groupedOptions).forEach(([groupLabel, groupOptions]) => {
      const filteredOptions = groupOptions.filter((option) =>
        option.label.toLowerCase().includes(query) ||
        groupLabel.toLowerCase().includes(query)
      );

      if (filteredOptions.length > 0) {
        filtered[groupLabel] = filteredOptions;
      }
    });

    return filtered;
  }, [groupedOptions, searchQuery]);

  // Get all visible post type values (across all filtered groups)
  const allVisiblePostTypes = useMemo(() => {
    const allValues: PostTypeValue[] = [];
    Object.values(filteredGroups).forEach((groupOptions) => {
      groupOptions.forEach((option) => {
        allValues.push(option.value);
      });
    });
    return allValues;
  }, [filteredGroups]);

  // Calculate select all state for currently visible post types
  const getSelectAllState = useMemo(() => {
    const selectedVisibleValues = localValue.filter((value) => allVisiblePostTypes.includes(value));

    return {
      checked: allVisiblePostTypes.length > 0 && selectedVisibleValues.length === allVisiblePostTypes.length,
      indeterminate: selectedVisibleValues.length > 0 && selectedVisibleValues.length < allVisiblePostTypes.length,
      disabled: allVisiblePostTypes.length === 0 || isUpdating
    };
  }, [allVisiblePostTypes, localValue, isUpdating]);

  // Handle select all/deselect all for currently visible post types
  const handleSelectAllToggle = useCallback(() => {
    if (isUpdating) {
      return;
    }

    const selectedVisibleValues = localValue.filter((value) => allVisiblePostTypes.includes(value));

    let newSelection: PostTypeValue[];

    if (selectedVisibleValues.length === allVisiblePostTypes.length) {
      // Deselect all visible post types (preserve selections from other filter contexts)
      newSelection = localValue.filter((value) => !allVisiblePostTypes.includes(value));
    } else {
      // Select all visible post types (preserve existing selections)
      newSelection = Array.from(new Set([...localValue, ...allVisiblePostTypes]));
    }

    updateSelection(newSelection);
  }, [allVisiblePostTypes, localValue, updateSelection, isUpdating]);

  const displayOrder = ['Instagram', 'TikTok', 'YouTube', 'Pinterest', 'Other'];

  const togglePostType = useCallback((postType: PostTypeValue) => {
    if (isUpdating) return;

    const newValue = localValue.includes(postType)
      ? localValue.filter((v) => v !== postType)
      : [...localValue, postType];

    updateSelection(newValue);
  }, [localValue, updateSelection, isUpdating]);

  const hasAnyResults = Object.keys(filteredGroups).length > 0;

  return (
    <>
      {/* Select All/Deselect All - always shown as first item when there are options */}
      {options.length > 0 && (
        <CommandSelectAll
          checked={getSelectAllState.checked}
          indeterminate={getSelectAllState.indeterminate}
          disabled={getSelectAllState.disabled}
          onToggle={handleSelectAllToggle}
          count={allVisiblePostTypes.length}
          data-dd-action-name="social-analytics-post-type-select-all"
        />
      )}

      {hasAnyResults ? (
        displayOrder
          .filter((label) => filteredGroups[label] && filteredGroups[label].length > 0)
          .map((label) => (
            <CommandSelectGroup key={label} heading={label}>
              {filteredGroups[label].map(({ value: postTypeValue, label: postTypeLabel }) => (
                <CommandSelectItem
                  key={postTypeValue}
                  value={postTypeValue}
                  onSelect={() => togglePostType(postTypeValue)}
                  selected={localValue.includes(postTypeValue)}
                  disabled={isUpdating}
                  className="pl-4"
                  data-dd-action-name="social-analytics-post-type-select"
                >
                  <span>{postTypeLabel}</span>
                </CommandSelectItem>
              ))}
            </CommandSelectGroup>
          ))
      ) : (
        <CommandSelectEmpty>No post types found.</CommandSelectEmpty>
      )}
    </>
  );
};
